import { useSelector } from 'react-redux';

import { usePlanFeatureGate } from './use-plan-feature-gate';
import { Features } from '../constants/features';
import { BillingPlanIds } from '../enums/billing-plan-ids';
import { MarketplaceIds } from '../enums/marketplace-ids';
import { useIsTrialActive } from '../hooks/use-is-trial-active';
import Global from '../store/global-selectors';

/** @typedef {import('../prop-types/intl-types').MessageDescriptor} MessageDescriptor */

/**
 *
 * @returns {{ isFeatureEnabled: boolean, disabledMessage?: MessageDescriptor}}
 */
export const useExternalStoresAccessFeatureGate = () => usePlanFeatureGate(Features.ExternalStoresAccess);

// Hook to determine if the user can have one active store while on the free plan
// In this scenario, a user will have the ExternalStoresAccess Feature enabled
export const useHasSingleOrderSourceForFreePlan = () => {
  const { isFeatureEnabled: isExternalStoresAccessFeatureEnabled } = useExternalStoresAccessFeatureGate();
  const { billingPlanId } = useSelector(Global.selectSellerSubscriptionPlanInfo) ?? {};
  const isTrial = useIsTrialActive();
  const isFreePlan = billingPlanId === BillingPlanIds.Free2025Q2;

  return isExternalStoresAccessFeatureEnabled && isFreePlan && !isTrial;
};

// Hook to determine if the user can add an external store
export const useCanAddExternalStore = () => {
  const { isFeatureEnabled: isExternalStoresAccessFeatureEnabled } = useExternalStoresAccessFeatureGate();
  const canHaveExternalOrderSource = useHasSingleOrderSourceForFreePlan();

  const activeSellerStores = useSelector(Global.selectSellerActiveStores);
  const hasActiveExternalStore = activeSellerStores.some(
    (store) => store.marketplaceId !== MarketplaceIds.ShipStation && store.marketplaceId !== MarketplaceIds.CustomStore,
  );

  if (canHaveExternalOrderSource) {
    return !hasActiveExternalStore;
  }

  return isExternalStoresAccessFeatureEnabled;
};
